<div class="space-y-4">
  <app-breadcrumbs [items]="breadcrumbItems()" />

  <section class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
    <div class="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 pb-4">
      <div>
        <div class="text-sm font-medium uppercase tracking-wide text-zinc-500">
          User Profile
        </div>

        <h1 class="mt-1 text-2xl font-semibold tracking-tight text-zinc-900 sm:text-3xl">
          {{ displayName() }}
        </h1>
      </div>

      @if (showAccountStatus() && user(); as userModel) {
        <div class="mt-4 flex items-center justify-end gap-2">
          <span class="text-xs font-semibold uppercase tracking-wide text-zinc-500">Status</span>
          <div
            class="account-status-segmented"
            [class.is-enabled]="userModel.isActive"
            [class.is-disabled]="!userModel.isActive"
            [class.is-updating]="updatingStatus()"
          >
            <span class="account-status-segmented__thumb" aria-hidden="true"></span>
            <button
              type="button"
              (click)="setUserStatus(true)"
              [disabled]="updatingStatus() || userModel.isActive"
              [class.is-active]="userModel.isActive"
              class="account-status-segmented__button"
            >
              Enabled
            </button>

            <button
              type="button"
              (click)="setUserStatus(false)"
              [disabled]="updatingStatus() || !userModel.isActive"
              [class.is-active]="!userModel.isActive"
              class="account-status-segmented__button"
            >
              Disabled
            </button>
          </div>
        </div>
      }
    </div>

    @if (loading()) {
      <div class="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
        Loading user profile...
      </div>
    } @else if (errorMessage()) {
      <div class="mt-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {{ errorMessage() }}
      </div>
    } @else {
      @if (user(); as userModel) {
        <div class="mt-5 grid gap-5 lg:grid-cols-2">
          <section class="space-y-4 rounded-xl border border-slate-200 p-4">
            <div class="flex flex-wrap items-center justify-between gap-2">
              <h2 class="text-base font-semibold text-slate-900">Profile Details</h2>
            </div>

            @if (showProfileAuditDetails()) {
              <div class="space-y-2 text-xs text-slate-500">
                <div>Created: <span class="font-medium text-slate-700">{{ userModel.createdAt | date:'mediumDate' }}</span></div>
                <div>Last Updated: <span class="font-medium text-slate-700">{{ userModel.updatedAt | date:'medium' }}</span></div>
                @if(userModel.lastUpdatedBy) {
                  <div>
                    Updated By:
                    <span class="font-medium text-slate-700">
                      {{ userModel.lastUpdatedBy.fullName }}
                      @if (userModel.lastUpdatedBy.email) {
                        ({{ userModel.lastUpdatedBy.email }})
                      }
                    </span>
                  </div>
                }
              </div>
            }

            <form [formGroup]="profileForm" (ngSubmit)="saveProfile()" class="space-y-4">
              <div>
                <label class="mb-1 block text-sm font-medium text-slate-700">Full Name</label>
                <input
                  type="text"
                  formControlName="fullName"
                  class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                />
              </div>

              <div>
                <label class="mb-1 block text-sm font-medium text-slate-700">Email</label>
                <input
                  type="email"
                  formControlName="email"
                  class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                />
              </div>

              <div>
                <label class="mb-1 block text-sm font-medium text-slate-700">Role</label>
                <select
                  formControlName="role"
                  class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                >
                  <option value="admin">Admin</option>
                  <option value="dekalb">Dekalb</option>
                  <option value="partner">Partner</option>
                </select>
              </div>

              @if (profileForm.controls.role.value === 'partner') {
                <div>
                  <label class="mb-1 block text-sm font-medium text-slate-700">Partner Organization</label>
                  <select
                    formControlName="partnerOrganizationId"
                    class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                  >
                    <option value="">Select a partner organization</option>
                    @for (partner of partners(); track partner.id) {
                      <option [value]="partner.id">{{ partner.name }}</option>
                    }
                  </select>
                  @if (loadingPartners()) {
                    <p class="mt-1 text-xs text-slate-500">Loading partner options...</p>
                  }
                </div>
              }

              <div class="flex justify-end">
                <button
                  type="submit"
                  [disabled]="savingProfile()"
                  class="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {{ savingProfile() ? 'Saving...' : 'Save Profile' }}
                </button>
              </div>
            </form>
          </section>

          <div class="space-y-4">
            <section class="rounded-xl border border-slate-200 p-4">
              <h2 class="text-base font-semibold text-slate-900">{{ passwordSectionTitle() }}</h2>
              <p class="mt-1 text-sm text-slate-500">
                {{ passwordSectionDescription() }}
              </p>

              <form [formGroup]="resetPasswordForm" (ngSubmit)="resetPassword()" class="mt-4 space-y-4">
                <div>
                  <label class="mb-1 block text-sm font-medium text-slate-700">New Password</label>
                  <input
                    type="password"
                    formControlName="password"
                    class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                  />
                  <p class="mt-1 text-xs text-slate-500">Must include at least 8 characters, uppercase, lowercase, and a number.</p>
                </div>

                <div>
                  <label class="mb-1 block text-sm font-medium text-slate-700">Confirm Password</label>
                  <input
                    type="password"
                    formControlName="confirmPassword"
                    class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                  />
                  @if (resetPasswordForm.errors?.['passwordMismatch']) {
                    <p class="mt-1 text-xs text-red-600">Passwords do not match.</p>
                  }
                </div>

                <div class="flex justify-end">
                  <button
                    type="submit"
                    [disabled]="resettingPassword()"
                    class="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {{ resettingPassword() ? passwordSubmittingLabel() : passwordSubmitLabel() }}
                  </button>
                </div>
              </form>
            </section>
          </div>
        </div>
      }
    }
  </section>
</div>

@if (pendingStatusChange() !== null) {
  <div class="fixed inset-0 z-40 bg-slate-900/50" (click)="cancelStatusChange()"></div>

  <div class="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="user-status-modal-title">
    <div class="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
      <h3 id="user-status-modal-title" class="text-lg font-semibold text-slate-900">
        {{ pendingStatusActionLabel() }}?
      </h3>
      <p class="mt-2 text-sm text-slate-600">
        {{ pendingStatusDescription() }}
      </p>
      <p class="mt-3 text-sm text-slate-600">
        This change applies to {{ displayName() }}.
      </p>

      <div class="mt-5 flex flex-wrap justify-end gap-2">
        <button
          type="button"
          (click)="cancelStatusChange()"
          [disabled]="updatingStatus()"
          class="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Cancel
        </button>
        <button
          type="button"
          (click)="confirmStatusChange()"
          [disabled]="updatingStatus()"
          [ngClass]="pendingStatusChange() ? 'bg-emerald-700 hover:bg-emerald-600' : 'bg-red-700 hover:bg-red-600'"
          class="rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          {{ updatingStatus() ? 'Updating...' : pendingStatusActionLabel() }}
        </button>
      </div>
    </div>
  </div>
}
